import React, { useState, useEffect } from 'react';
import { Row, Col, Card, DatePicker, Button, message, Space, Divider, Tabs, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faChartBar, faPhone, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { faGoogle as faGoogleBrand } from '@fortawesome/free-brands-svg-icons';
import moment from 'moment';
import axios from 'axios';

import CSVImportCard from './components/CSVImportCard';
// import CampaignConfig from './components/CampaignConfig'; // Not needed - campaigns from Google Ads
import CallVolumeChart from './components/CallVolumeChart';
import CallVolumeTable from './components/CallVolumeTable';
import RingCentralCallsTable from '../CallVolume/RingCentralCallsTable';
import GoogleAdsCampaignsTable from '../CallVolume/GoogleAdsCampaignsTable';
import { callAnalyticsAPI } from '../../services/callAnalytics';

const { RangePicker } = DatePicker;
const { Option } = Select;

const CallVolumeTracking = () => {
  const [selectedPreset, setSelectedPreset] = useState(90); // Default to 3 months
  const [dateRange, setDateRange] = useState([
    moment().subtract(90, 'days'),
    moment()
  ]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aggregating, setAggregating] = useState(false);
  
  const handlePresetChange = (days) => {
    setSelectedPreset(days);
    setDateRange([
      moment().subtract(days, 'days'),
      moment()
    ]);
  };

  // Load available campaigns on mount
  useEffect(() => {
    loadAvailableCampaigns();
  }, []);

  // Load daily stats when date range or campaign changes
  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      loadDailyStats();
    }
  }, [dateRange, selectedCampaign]);

  const loadAvailableCampaigns = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/call-analytics/available-campaigns');
      setAvailableCampaigns(response.data.data || []);
    } catch (error) {
      console.error('Error loading available campaigns:', error);
    }
  };

  const loadDailyStats = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) return;

    setLoading(true);
    try {
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD')
      };

      if (selectedCampaign && selectedCampaign !== 'all') {
        params.campaignName = selectedCampaign;
      }

      const data = await callAnalyticsAPI.getDailyStats(params);
      setDailyStats(data);
    } catch (error) {
      message.error('Failed to load daily statistics');
      console.error('Load daily stats error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAggregateData = async (grouped = false) => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('Please select a date range first');
      return;
    }

    setAggregating(true);
    try {
      const params = {
        startDate: dateRange[0].format('YYYY-MM-DD'),
        endDate: dateRange[1].format('YYYY-MM-DD')
      };

      let result;
      if (grouped) {
        result = await callAnalyticsAPI.aggregateGroupedData(params);
        message.success(`Successfully aggregated grouped data - ${result.results.processed} records processed`);
      } else {
        result = await callAnalyticsAPI.aggregateData(params);
        message.success(`Successfully aggregated ${result.daysProcessed || result.results?.processed || 0} days of data`);
      }
      
      // Reload the stats after aggregation
      await loadDailyStats();
    } catch (error) {
      message.error(error.response?.data?.error || 'Failed to aggregate data');
      console.error('Aggregate data error:', error);
    } finally {
      setAggregating(false);
    }
  };

  const handleImportSuccess = () => {
    message.success('Data imported successfully. Click "Aggregate Data" to update statistics.');
  };

  return (
    <div>
      {/* Import Section */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={12}>
          <CSVImportCard
            title="RingCentral Call Logs"
            type="ringcentral"
            onSuccess={handleImportSuccess}
            description="Upload RingCentral CSV export with call details"
            acceptedFormats=".csv"
          />
        </Col>
        <Col span={12}>
          <CSVImportCard
            title="Google Ads Campaign Report"
            type="google-ads"
            onSuccess={handleImportSuccess}
            description="Upload Google Ads campaign report CSV"
            acceptedFormats=".csv"
          />
        </Col>
      </Row>

      {/* Campaign Configuration - Hidden (not needed for date-only matching) */}
      {/* Uncomment if you need to map phone numbers to specific campaigns */}
      {/* <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <CampaignConfig 
            campaigns={campaigns}
            onUpdate={handleCampaignUpdate}
          />
        </Col>
      </Row> */}

      <Divider />

      {/* Analytics and Data View Tabs */}
      <Tabs
        defaultActiveKey="1"
        items={[
          {
            key: '1',
            label: (
              <span>
                <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 8 }} />
                Comparison Analytics
              </span>
            ),
            children: (
              <Card 
                style={{ borderRadius: '12px' }}
                title={
                  <Space>
                    <FontAwesomeIcon icon={faChartBar} />
                    <span>Call Volume Comparison</span>
                  </Space>
                }
                extra={
                  <Space>
                    <Select
                      value={selectedPreset}
                      onChange={handlePresetChange}
                      style={{ width: 150, height: '48px' }}
                      placeholder="Quick Select"
                      size="large"
                    >
                      <Option value={7}>Last 7 Days</Option>
                      <Option value={14}>Last 14 Days</Option>
                      <Option value={30}>Last 30 Days</Option>
                      <Option value={90}>Last 3 Months</Option>
                      <Option value={180}>Last 6 Months</Option>
                    </Select>
                    <Select
                      value={selectedCampaign}
                      onChange={setSelectedCampaign}
                      style={{ width: 200, height: '48px' }}
                      size="large"
                    >
                      <Option value="all">All Campaigns</Option>
                      {availableCampaigns.map(c => (
                        <Option key={c.campaignName} value={c.campaignName}>
                          {c.campaignName}
                        </Option>
                      ))}
                    </Select>
                    <RangePicker
                      value={dateRange}
                      onChange={(dates) => {
                        setDateRange(dates);
                        setSelectedPreset(null); // Clear preset when manually selecting dates
                      }}
                      format="YYYY-MM-DD"
                      style={{ width: 280, height: '48px' }}
                    />
                    <Button
                      type="primary"
                      icon={<FontAwesomeIcon icon={faSync} />}
                      onClick={() => handleAggregateData(false)}
                      loading={aggregating}
                      size="large"
                      style={{ height: '48px', borderRadius: '8px' }}
                    >
                      Aggregate Data
                    </Button>
                    <Button
                      type="default"
                      icon={<FontAwesomeIcon icon={faChartBar} />}
                      onClick={() => handleAggregateData(true)}
                      loading={aggregating}
                      size="large"
                      style={{ height: '48px', borderRadius: '8px' }}
                    >
                      Aggregate Grouped
                    </Button>
                  </Space>
                }
              >
                {/* Chart */}
                <div style={{ marginBottom: '32px' }}>
                  <CallVolumeChart 
                    data={dailyStats}
                    loading={loading}
                  />
                </div>

                {/* Table */}
                <CallVolumeTable 
                  data={dailyStats}
                  loading={loading}
                  onRefresh={loadDailyStats}
                />
              </Card>
            )
          },
          {
            key: '2',
            label: (
              <span>
                <FontAwesomeIcon icon={faPhone} style={{ marginRight: 8 }} />
                RingCentral Calls
              </span>
            ),
            children: <RingCentralCallsTable />
          },
          {
            key: '3',
            label: (
              <span>
                <FontAwesomeIcon icon={faGoogleBrand} style={{ marginRight: 8 }} />
                Google Ads Data
              </span>
            ),
            children: <GoogleAdsCampaignsTable />
          }
        ]}
      />
    </div>
  );
};

export default CallVolumeTracking;
